import { useState } from "react";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Mail, CheckCircle2, Store } from "lucide-react";

export function ForgotPasswordDialog({ open, onOpenChange, defaultEmail = "", shopSlug = null, shop = null }) {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, shop_slug: shopSlug || undefined });
      setSent(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const close = () => { setSent(false); setError(""); onOpenChange(false); };

  const hasLogo = !!(shop?.id && shop?.logo_path);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="forgot-password-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        {shop?.name && (
          <div data-testid="forgot-shop-header" className="flex items-center gap-3 pb-3 border-b border-zinc-900">
            {hasLogo ? (
              <img src={shopLogoUrl(shop.id)} alt={shop.name} className="h-10 w-auto max-w-[120px] object-contain" />
            ) : (
              <Store className="h-6 w-6" style={{ color: "var(--c-brand)" }} />
            )}
            <span className="font-display font-bold uppercase text-zinc-100 text-sm">{shop.name}</span>
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2">
            <Mail className="h-5 w-5" style={{ color: "var(--c-brand)" }} /> Esqueci minha senha
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Enviaremos um link válido por 1 hora para redefinir sua senha.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div data-testid="forgot-password-sent" className="rounded-xl p-4 text-sm flex items-start gap-2 border"
               style={{ borderColor: "rgb(var(--c-brand-rgb) / 0.4)", color: "var(--c-brand-neon)", background: "rgb(var(--c-brand-rgb) / 0.05)" }}>
            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
            <span>Se existir uma conta para esse e-mail, o link foi enviado. Verifique sua caixa e o spam.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">E-mail da conta</Label>
              <Input data-testid="forgot-password-email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[var(--c-brand)]"
                     placeholder="voce@email.com" />
            </div>
            {error && <div data-testid="forgot-password-error" className="text-sm text-red-400">{error}</div>}
            <Button data-testid="forgot-password-submit" type="submit" disabled={loading}
                    style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}
                    className="w-full h-11 font-bold uppercase hover:brightness-110">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar link"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
